import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { Loader2, Trash2, Phone, MessageCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getLeads, updateLeadStatus, deleteLead } from "@/lib/adminApi";

const STATUS_LABELS = { new: "Yeni", contacted: "Arandı", booked: "Rezervasyon", closed: "Kapandı" };
const STATUS_STYLES = {
  new: "bg-[#C8A24A]/20 text-[#8a6d1e] border-[#C8A24A]/40",
  contacted: "bg-blue-500/15 text-blue-700 border-blue-500/30",
  booked: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  closed: "bg-gray-400/15 text-gray-600 border-gray-400/30",
};

const STAT_CARDS = [
  { key: "all", label: "Toplam" },
  { key: "new", label: "Yeni" },
  { key: "contacted", label: "Arandı" },
  { key: "booked", label: "Rezervasyon" },
];

export default function LeadsPanel() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const { data, isLoading } = useQuery({ queryKey: ["admin-leads", filter], queryFn: () => getLeads(filter) });
  const leads = (data && data.items) || [];
  const counts = (data && data.counts) || {};

  const changeStatus = async (id, status) => {
    try {
      await updateLeadStatus(id, status);
      toast.success("Durum güncellendi");
      qc.invalidateQueries({ queryKey: ["admin-leads"] });
    } catch {
      toast.error("Güncellenemedi");
    }
  };
  const removeLead = async (id) => {
    if (!window.confirm("Bu talep silinsin mi?")) return;
    try {
      await deleteLead(id);
      toast.success("Talep silindi");
      qc.invalidateQueries({ queryKey: ["admin-leads"] });
    } catch {
      toast.error("Silinemedi");
    }
  };

  const fmtDate = (iso) => {
    try { return format(parseISO(iso), "d MMM yyyy HH:mm", { locale: tr }); } catch { return iso; }
  };

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {STAT_CARDS.map((s) => (
          <div key={s.key} className="rounded-xl bg-[#141517] border border-white/10 p-4">
            <div className="text-3xl font-display text-[#D7B86A]">{counts[s.key] ?? 0}</div>
            <div className="text-xs uppercase tracking-wide text-[#9A9387] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 bg-[#141517] border-white/15 text-[#EDE7DA]" data-testid="leads-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="new">Yeni</SelectItem>
            <SelectItem value="contacted">Arandı</SelectItem>
            <SelectItem value="booked">Rezervasyon</SelectItem>
            <SelectItem value="closed">Kapandı</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => qc.invalidateQueries({ queryKey: ["admin-leads"] })}
          className="border-white/15 text-[#EDE7DA] bg-transparent hover:bg-white/5">
          <RefreshCw className="w-4 h-4 mr-2" /> Yenile
        </Button>
      </div>

      {isLoading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8A24A]" /></div>
      ) : leads.length === 0 ? (
        <div className="py-20 text-center text-[#9A9387]">Henüz talep yok.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead className="bg-[#141517] text-[#9A9387] text-left">
              <tr>
                <th className="p-3 font-medium">Tarih</th>
                <th className="p-3 font-medium">Ad Soyad</th>
                <th className="p-3 font-medium">Telefon</th>
                <th className="p-3 font-medium">Düğün Tarihi</th>
                <th className="p-3 font-medium">İlçe</th>
                <th className="p-3 font-medium">Mesaj</th>
                <th className="p-3 font-medium">Durum</th>
                <th className="p-3 font-medium text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/8">
              {leads.map((l) => (
                <tr key={l.id} className="bg-[#0E0F10] hover:bg-[#141517] text-[#EDE7DA]">
                  <td className="p-3 whitespace-nowrap text-[#9A9387]">{fmtDate(l.created_at)}</td>
                  <td className="p-3 font-medium">{l.name}</td>
                  <td className="p-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span>{l.phone}</span>
                      <a href={`tel:${l.phone}`} className="text-[#C8A24A]" title="Ara"><Phone className="w-4 h-4" /></a>
                      <a href={`https://wa.me/${(l.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="text-[#25D366]" title="WhatsApp"><MessageCircle className="w-4 h-4" /></a>
                    </div>
                  </td>
                  <td className="p-3 whitespace-nowrap">{l.wedding_date || "-"}</td>
                  <td className="p-3 whitespace-nowrap">{l.district || "-"}</td>
                  <td className="p-3 max-w-[220px] truncate text-[#B8B1A5]" title={l.message || ""}>{l.message || "-"}</td>
                  <td className="p-3">
                    <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                      <SelectTrigger className={`h-8 w-36 border ${STATUS_STYLES[l.status] || ""}`} data-testid="lead-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(STATUS_LABELS).map((k) => (
                          <SelectItem key={k} value={k}>{STATUS_LABELS[k]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => removeLead(l.id)} className="text-[#9A9387] hover:text-red-400" title="Sil" data-testid="lead-delete-button">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
